import { useCallback, useEffect, useState } from "react";
import { getTopShared, type TopSharedRow } from "@/services/shareService";
import { isApiConfigured } from "@/services/http";

/** Sample rows shown while the live API is not connected (demo mode). */
const DEMO_SHARES: TopSharedRow[] = [
  { entity_slug: "manipal-wellness-hospital", entity_name: "Manipal Wellness Hospital", entity_type: "business", shares: 42 },
  { entity_slug: "mangalore-pearl-beach-resort", entity_name: "Mangalore Pearl Beach Resort", entity_type: "business", shares: 37 },
  { entity_slug: "mangalore-harbour-fish-house", entity_name: "Mangalore Harbour Fish House", entity_type: "business", shares: 31 },
  { entity_slug: "kundapura-lighthouse-beach-resort", entity_name: "Kundapura Lighthouse Beach Resort", entity_type: "business", shares: 26 },
  { entity_slug: "panambur-beach", entity_name: "Panambur Beach", entity_type: "spot", shares: 24 },
  { entity_slug: "sri-lakshmi-sarees-textiles", entity_name: "Sri Lakshmi Sarees & Textiles", entity_type: "business", shares: 19 },
  { entity_slug: "malpe-beach", entity_name: "Malpe Beach", entity_type: "spot", shares: 17 },
  { entity_slug: "mangalore-tech-solutions", entity_name: "Mangalore Tech Solutions", entity_type: "business", shares: 12 },
];

export default function ShareInsights() {
  const [rows, setRows] = useState<TopSharedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (!isApiConfigured()) {
        await new Promise((r) => setTimeout(r, 400));
        setRows(DEMO_SHARES);
        return;
      }
      const data = await getTopShared(10);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load share insights.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const max = Math.max(...rows.map((r) => r.shares), 1);
  const totalShares = rows.reduce((sum, r) => sum + r.shares, 0);

  return (
    <div className="rounded-xl border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-6 py-4">
        <div>
          <h3 className="font-heading text-sm font-bold text-foreground-950">Most Shared Listings</h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            Listings shared most often via the social buttons on their detail pages.
          </p>
        </div>
        <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
          {totalShares} total shares
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-12">
          <p className="text-xs text-foreground-500">{error}</p>
          <button
            type="button"
            onClick={load}
            className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-share-line text-xl" />
          </span>
          <p className="text-xs text-foreground-500">
            No shares tracked yet. Interact with the share buttons on any listing to see data here.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-background-100">
          {rows.map((row, i) => (
            <div key={`${row.entity_type}-${row.entity_slug}`} className="flex items-center gap-4 px-6 py-3.5">
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  i === 0
                    ? "bg-accent-100 text-accent-900"
                    : "bg-background-100 text-foreground-600"
                }`}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-xs font-semibold text-foreground-900">
                    {row.entity_name ?? row.entity_slug}
                  </p>
                  <span className="shrink-0 rounded-full bg-background-100 px-2 py-0.5 text-[10px] font-medium capitalize text-foreground-500">
                    {row.entity_type}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className="h-1.5 rounded-full bg-primary-500 transition-all duration-500"
                    style={{ width: `${Math.round((row.shares / max) * 100)}%` }}
                  />
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-bold tabular-nums text-foreground-950">{row.shares}</p>
                <p className="text-[10px] text-foreground-400">shares</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}